/**
 * Client review index for Marco 2 — every route points at an existing prototype screen.
 * `preview` describes a lightweight static thumbnail of that screen's layout; the real
 * screen only loads when the reviewer presses "Ver tela".
 */

export type MobilePreviewBlock =
  | 'summary'
  | 'progress'
  | 'habits'
  | 'cta'
  | 'bars'
  | 'rail'
  | 'post'
  | 'chips'
  | 'tabs'
  | 'line'
  | 'heatmap'
  | 'rows'
  | 'stats'
  | 'hive'
  | 'gate'
  | 'profile'
  | 'days'
  | 'ring'
  | 'mood'

export type AdminPreviewKind =
  | 'kpis'
  | 'line'
  | 'bars'
  | 'table'
  | 'tabs'
  | 'filters'
  | 'matrix'
  | 'form'
  | 'rule'
  | 'cards'
  | 'donut'
  | 'log'

export interface AdminPreviewBlock {
  kind: AdminPreviewKind
  /** Spans the full content width instead of one of two columns. */
  wide?: boolean
}

interface ReviewScreenBase {
  id: string
  name: string
  description: string
  route: string
}

export interface MobileReviewScreen extends ReviewScreenBase {
  /** Title shown in the thumbnail's header, when it differs from the card name. */
  heading?: string
  preview: MobilePreviewBlock[]
}

export interface AdminReviewScreen extends ReviewScreenBase {
  preview: AdminPreviewBlock[]
}

export const mobileReviewScreens: MobileReviewScreen[] = [
  { id: 'home', name: 'Home', heading: 'Bom dia, Ana.', description: 'Visão pessoal do dia: Life Score, Daily Check, Bee de hoje, sequência e pendências.', route: '/', preview: ['summary', 'habits', 'cta', 'bars'] },
  { id: 'check', name: 'Daily Check', description: 'Seis hábitos que somam 100 Bee, humor e meta de hidratação personalizada.', route: '/check', preview: ['progress', 'habits', 'mood'] },
  { id: 'lifescore', name: 'Life Score', description: 'Bem-estar de hoje em escala de 0 a 100, com resumos de semana, mês e ano.', route: '/stats/lifescore', preview: ['tabs', 'ring', 'stats'] },
  { id: 'progress', name: 'Progresso', description: 'Consistência, heatmap de 90 dias, revisão semanal, humor e metas.', route: '/stats', preview: ['summary', 'heatmap', 'rows'] },
  { id: 'community', name: 'Comunidade', description: 'Pulse, feed, comunidades por interesse, desafios e descoberta.', route: '/comunidade', preview: ['tabs', 'rail', 'post'] },
  { id: 'create', name: 'Criar Post / Pulse', description: 'Pulse sempre disponível; Post liberado com 50 Bee de base do Daily Check.', route: '/novo', preview: ['gate', 'rows'] },
  { id: 'profile', name: 'Perfil', description: 'Perfil próprio, conquistas, controles do perfil público e acesso à saúde.', route: '/perfil', preview: ['profile', 'chips', 'rows'] },
  { id: 'health', name: 'Saúde', description: 'Métricas corporais e biomarcadores, privados por padrão.', route: '/perfil/saude', preview: ['stats', 'rows'] },
  { id: 'biomarkers', name: 'Biomarcadores', description: 'Resultados laboratoriais como Testosterona Total, sem interpretação clínica.', route: '/perfil/saude/biomarcadores', preview: ['stats', 'line', 'rows'] },
  { id: 'hive', name: 'Minha Colmeia', description: 'Colmeia em cinco estágios que cresce com os hábitos, e o Baú da Colmeia.', route: '/perfil/colmeia', preview: ['hive', 'cta', 'rows'] },
  { id: 'engagement', name: 'Engajamento e Gamificação', heading: 'Proteção de Sequência', description: 'Área que reúne Proteção de Sequência, Minha Colmeia, Baú da Colmeia, desafios e Liga. Abre em Proteção de Sequência.', route: '/stats/protecao', preview: ['days', 'stats', 'rows'] },
  { id: 'settings', name: 'Configurações', description: 'Conta, idioma, unidades, notificações, privacidade, segurança e serviços conectados.', route: '/config', preview: ['rows', 'rows', 'rows'] },
]

export const adminReviewScreens: AdminReviewScreen[] = [
  { id: 'dashboard', name: 'Dashboard', description: 'Saúde da plataforma em segundos, com janela de tempo explícita em cada indicador.', route: '/admin', preview: [{ kind: 'kpis', wide: true }, { kind: 'line' }, { kind: 'bars' }] },
  { id: 'users', name: 'Usuários', description: 'Gestão de contas sem expor dados de saúde na lista.', route: '/admin/usuarios', preview: [{ kind: 'filters', wide: true }, { kind: 'table', wide: true }] },
  { id: 'team', name: 'Equipe', description: 'Membros da equipe e mudanças de acesso com confirmação e auditoria.', route: '/admin/equipe', preview: [{ kind: 'table', wide: true }, { kind: 'cards', wide: true }] },
  { id: 'roles', name: 'Papéis e permissões', description: 'Papéis, permissões especiais e acesso excepcional a dados sensíveis.', route: '/admin/papeis', preview: [{ kind: 'cards', wide: true }, { kind: 'matrix', wide: true }] },
  { id: 'content', name: 'Conteúdo', description: 'Conteúdo do produto em pt-BR e en-US, publicado só com as duas versões.', route: '/admin/conteudo', preview: [{ kind: 'tabs', wide: true }, { kind: 'table', wide: true }] },
  { id: 'moderation', name: 'Moderação', description: 'Filas de posts, Pulses e comentários com decisões auditadas.', route: '/admin/moderacao/post', preview: [{ kind: 'tabs', wide: true }, { kind: 'table', wide: true }] },
  { id: 'reports', name: 'Denúncias', description: 'Fila de denúncias por status, com histórico e resolução.', route: '/admin/denuncias', preview: [{ kind: 'tabs', wide: true }, { kind: 'table', wide: true }] },
  { id: 'notifications', name: 'Notificações', description: 'Campanhas bilíngues; públicos de saúde mostram só definição e tamanho.', route: '/admin/notificacoes', preview: [{ kind: 'kpis', wide: true }, { kind: 'table', wide: true }] },
  { id: 'dailycheck', name: 'Daily Check Analytics', description: 'Participação, conclusão e abandono por hábito, sempre agregados.', route: '/admin/daily-check', preview: [{ kind: 'kpis', wide: true }, { kind: 'line' }, { kind: 'bars' }] },
  { id: 'gamification', name: 'Gamificação', description: 'Regras fixas separadas dos parâmetros configuráveis de engajamento.', route: '/admin/gamificacao/xp', preview: [{ kind: 'tabs', wide: true }, { kind: 'rule' }, { kind: 'form' }] },
  { id: 'performance', name: 'Performance', description: 'Adoção e uso de métricas por modalidade esportiva, agregados.', route: '/admin/performance', preview: [{ kind: 'kpis', wide: true }, { kind: 'bars' }, { kind: 'donut' }] },
  { id: 'community', name: 'Comunidade', description: 'Criação, engajamento, equilíbrio saúde/social e conformidade do gate de Post.', route: '/admin/comunidade', preview: [{ kind: 'kpis', wide: true }, { kind: 'line', wide: true }] },
  { id: 'exports', name: 'Relatórios', description: 'Exportações com escopo de dados declarado por tipo de relatório.', route: '/admin/relatorios', preview: [{ kind: 'cards', wide: true }, { kind: 'table', wide: true }] },
  { id: 'audit', name: 'Auditoria', description: 'Log somente leitura das ações administrativas, com filtros.', route: '/admin/auditoria', preview: [{ kind: 'filters', wide: true }, { kind: 'log', wide: true }] },
  { id: 'settings', name: 'Configurações', description: 'Geral, marca, idiomas, notificações, moderação, segurança e manutenção.', route: '/admin/config/geral', preview: [{ kind: 'tabs', wide: true }, { kind: 'form' }, { kind: 'form' }] },
  { id: 'hydration', name: 'Hidratação', description: 'Regra ativa de 35 ml por kg, alteração controlada e histórico de versões.', route: '/admin/config/hidratacao', preview: [{ kind: 'tabs', wide: true }, { kind: 'rule' }, { kind: 'table' }] },
  { id: 'integrations', name: 'Integrações', description: 'Status por provedor — todos "Em breve"; nenhuma conexão real.', route: '/admin/config/integracoes', preview: [{ kind: 'tabs', wide: true }, { kind: 'table', wide: true }] },
]

export interface Deliverable {
  title: string
  description: string
}

export const marco2Deliverables: Deliverable[] = [
  { title: 'UX/UI', description: 'Telas, navegação, fluxos e estados do App Mobile e do Admin Web.' },
  { title: 'Design System', description: 'Identidade BEELYV, componentes reutilizáveis, sistema de ícones e padrões visuais.' },
  { title: 'Regras do Produto', description: 'Daily Check, Life Score, Bee, hidratação, privacidade, comunidade e engajamento.' },
  { title: 'Fundação Técnica', description: 'Arquitetura do sistema, modelo de dados inicial, fundação da API, autenticação e autorização, ambientes, armazenamento, integrações e auditoria.' },
]

export const notInThisDelivery: string[] = [
  'backend de produção',
  'banco de dados de produção',
  'autenticação real',
  'armazenamento real de arquivos',
  'envio real de notificações',
  'versão Alpha funcional',
]
